import React, { useEffect, useMemo } from "react";
import { Card, Row, Col, Input, Radio } from "antd";
import NumberFormat from "react-number-format";
import RadioButton from "../../../../components/utils/radioButton";

const PartnershipCard = ({
  herds = [],
  values,
  setFieldValue,
  errors,
  submitCount,
  translation,
}) => {
  const reportType = values.partnershipContract?.reportType || null;
  const uniqueData = values.partnershipContract?.uniqueData || {
    carcassHarnessingPercentage: null,
  };
  const agreedWeightType = values.partnershipContract?.agreedWeightType || null;
  const maxBreakPercentage =
    values.partnershipContract?.maxBreakPercentage ?? null;
  const partnershipType = values.partnershipContract?.partnershipType || null;
  const partnerPercentage =
    values.partnershipContract?.partnerPercentage ?? null;

  useEffect(() => {
    if (
      values.partnershipContract?.herdsWithData &&
      reportType === "cjto" &&
      values.herds?.length > 0
    ) {
      const hasDataInHerds = values.herds.some(
        (h) => h.carcassHarnessingPercentage !== null,
      );
      if (!hasDataInHerds) {
        const updatedHerds = (values.herds || []).map((herd) => {
          const herdData = values.partnershipContract.herdsWithData.find(
            (h) => h.id === herd.id,
          );
          return {
            ...herd,
            carcassHarnessingPercentage:
              herdData?.carcassHarnessingPercentage ?? null,
          };
        });
        setFieldValue("herds", updatedHerds);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const herdsIds = (herds || [])
      .map((h) => h.id)
      .sort()
      .join(",");
    const valuesHerdsIds = (values.herds || [])
      .map((h) => h.id)
      .sort()
      .join(",");

    if (herdsIds !== valuesHerdsIds && herdsIds) {
      const updatedHerds = (herds || []).map((herd) => {
        const existingHerd = values.herds?.find((h) => h.id === herd.id);
        return {
          ...herd,
          carcassHarnessingPercentage:
            existingHerd?.carcassHarnessingPercentage ?? null,
        };
      });
      setFieldValue("herds", updatedHerds);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [herds]);

  const updatePartnershipContract = (updates) => {
    const currentContract = values.partnershipContract || {};
    const newPartnershipType = updates.partnershipType ?? partnershipType;
    const newReportType = updates.reportType ?? reportType;
    const newAgreedWeightType = updates.agreedWeightType ?? agreedWeightType;
    const newMaxBreakPercentage =
      updates.maxBreakPercentage ?? maxBreakPercentage;
    const newPartnerPercentage = updates.partnerPercentage ?? partnerPercentage;

    const herdsWithData =
      newReportType === "cjto"
        ? (values.herds || []).map((h) => ({
            id: h.id,
            carcassHarnessingPercentage: h.carcassHarnessingPercentage,
            numberOfDays: h.numberOfDays,
          }))
        : null;

    setFieldValue("partnershipContract", {
      ...currentContract,
      ...updates,
      partnershipType: newPartnershipType,
      reportType: newReportType,
      agreedWeightType: newAgreedWeightType,
      maxBreakPercentage: newMaxBreakPercentage,
      herdsWithData,
      uniqueData:
        newReportType === "unico" ? (updates.uniqueData ?? uniqueData) : null,
      partnerPercentage: newPartnerPercentage,
    });
  };

  const handlePartnershipTypeChange = (e) => {
    const updatedHerds = (values.herds || []).map((h) => ({
      ...h,
      carcassHarnessingPercentage: null,
      numberOfDays: null,
    }));
    setFieldValue("herds", updatedHerds);
    if (e.target.value === "CARCASS_HARNESSING") {
      updatePartnershipContract({
        partnershipType: e.target.value,
        reportType: "cjto",
        herdsWithData: updatedHerds,
        uniqueData: {
          carcassHarnessingPercentage: null,
          numberOfDays: null,
        },
        agreedWeightType: agreedWeightType,
        maxBreakPercentage: maxBreakPercentage,
        partnerPercentage: null,
      });
    } else {
      updatePartnershipContract({
        partnershipType: e.target.value,
        reportType: "cjto",
        herdsWithData: updatedHerds,
        uniqueData: {
          carcassHarnessingPercentage: null,
          numberOfDays: null,
        },
        agreedWeightType: null,
        maxBreakPercentage: null,
        partnerPercentage: null,
      });
    }
  };

  const handleReportTypeChange = (e) => {
    if (e.target.value === "unico") {
      const updatedHerds = (values.herds || []).map((h) => ({
        ...h,
        carcassHarnessingPercentage: null,
      }));
      setFieldValue("herds", updatedHerds);
      updatePartnershipContract({
        reportType: e.target.value,
        herdsWithData: updatedHerds,
        uniqueData: {
          carcassHarnessingPercentage: null,
        },
        agreedWeightType: agreedWeightType,
        maxBreakPercentage: maxBreakPercentage,
        partnerPercentage: null,
      });
    } else {
      updatePartnershipContract({
        reportType: e.target.value,
        uniqueData: {
          carcassHarnessingPercentage: null,
        },
        agreedWeightType: agreedWeightType,
        maxBreakPercentage: maxBreakPercentage,
        partnerPercentage: null,
      });
    }
  };

  const handleHerdDataChange = (herdId, field, value) => {
    const updatedHerds = (values.herds || []).map((h) =>
      h.id === herdId ? { ...h, [field]: value } : h,
    );
    setFieldValue("herds", updatedHerds);

    if (reportType === "cjto") {
      const herdsWithData = updatedHerds.map((h) => ({
        id: h.id,
        carcassHarnessingPercentage: h.carcassHarnessingPercentage,
      }));
      updatePartnershipContract({ herdsWithData });
    }
  };

  const handleUniqueDataChange = (field, value) => {
    const updatedUniqueData = { ...uniqueData, [field]: value };
    updatePartnershipContract({ uniqueData: updatedUniqueData });
  };

  const handleAgreedWeightTypeChange = (e) => {
    updatePartnershipContract({ agreedWeightType: e.target.value });
  };

  const handleMaxBreakPercentageChange = (value) => {
    updatePartnershipContract({ maxBreakPercentage: value });
  };

  const handlePartnerPercentageChange = (value) => {
    updatePartnershipContract({ partnerPercentage: value });
  };

  const showCarcassEntryByHerd = useMemo(() => {
    return reportType === "cjto";
  }, [reportType]);

  const showUniqueRow = () => {
    return reportType === "unico";
  };

  const t = translation.boitelContractsPage.form.partnershipContract;

  return (
    <Card
      style={{
        marginTop: 16,
        borderRadius: 7,
        background: "#fff",
        boxShadow: "0px 0px 10px #d8d8d8",
      }}
    >
      {/* Partnership Type */}
      <Row type="flex" gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Row>
            <label
              className={
                errors.partnershipContract?.partnershipType && submitCount > 0
                  ? "error"
                  : ""
              }
            >
              {t.partnershipType}*
            </label>
          </Row>
          <Row>
            <Radio.Group
              value={partnershipType}
              onChange={handlePartnershipTypeChange}
            >
              <Row type="flex" gutter={8}>
                <Col>
                  <RadioButton value="CARCASS_HARNESSING">
                    {t.partnershipTypeOptions.CARCASS_HARNESSING}
                  </RadioButton>
                </Col>
                <Col>
                  <RadioButton value="ARROBA_PRODUCTION">
                    {t.partnershipTypeOptions.ARROBA_PRODUCTION}
                  </RadioButton>
                </Col>
              </Row>
            </Radio.Group>
          </Row>
        </Col>
      </Row>
      <Row type="flex" gutter={16} style={{ marginBottom: 16 }}>
        {/* Report Type */}
        <Col span={8}>
          <Row>
            <label
              className={
                errors.partnershipContract?.reportType && submitCount > 0
                  ? "error"
                  : ""
              }
            >
              {t.report}*
            </label>
          </Row>
          <Row>
            <Radio.Group value={reportType} onChange={handleReportTypeChange}>
              <Row type="flex" gutter={8}>
                <Col>
                  <RadioButton value="cjto">{t.byLot}</RadioButton>
                </Col>
                <Col>
                  <RadioButton value="unico">{t.unique}</RadioButton>
                </Col>
              </Row>
            </Radio.Group>
          </Row>
        </Col>
        {/* Herd Data */}
        <Col span={8}>
          <Row type="flex" gutter={8}>
            <Col span={8}>
              <label style={{ fontWeight: "bold" }}>{t.cjt}</label>
            </Col>
            <Col span={8}>
              <label
                style={{ fontWeight: "bold" }}
                className={
                  (errors.partnershipContract?.herdsWithData?.some(
                    (h) => h.carcassHarnessingPercentage,
                  ) ||
                    errors.partnershipContract?.uniqueData
                      ?.carcassHarnessingPercentage) &&
                  submitCount > 0
                    ? "error"
                    : ""
                }
              >
                {t.carcassEntryPercentage}*
              </label>
            </Col>
            {partnershipType === "ARROBA_PRODUCTION" && (
              <Col span={8}>
                <label
                  style={{ fontWeight: "bold" }}
                  className={
                    (errors.partnershipContract?.herdsWithData?.some(
                      (h) => h.numberOfDays,
                    ) ||
                      errors.partnershipContract?.uniqueData?.numberOfDays) &&
                    submitCount > 0
                      ? "error"
                      : ""
                  }
                >
                  {t.numberOfDays}*
                </label>
              </Col>
            )}
          </Row>
          {(values.herds || []).map((herd, index) => (
            <Row
              key={herd.id}
              type="flex"
              gutter={8}
              style={{ marginBottom: 8 }}
            >
              <Col span={8}>
                <Input
                  value={index + 1}
                  disabled
                  style={{ textAlign: "center" }}
                />
              </Col>
              <Col span={8}>
                <NumberFormat
                  disabled={!showCarcassEntryByHerd}
                  customInput={Input}
                  value={herd.carcassHarnessingPercentage || ""}
                  placeholder={t.carcassEntryPercentagePlaceholder}
                  addonAfter="%"
                  decimalScale={2}
                  decimalSeparator=","
                  thousandSeparator="."
                  fixedDecimalScale={true}
                  onValueChange={({ floatValue }) =>
                    handleHerdDataChange(
                      herd.id,
                      "carcassHarnessingPercentage",
                      floatValue > 100 ? 100 : floatValue,
                    )
                  }
                  style={{ textAlign: "center" }}
                />
              </Col>
              {partnershipType === "ARROBA_PRODUCTION" && (
                <Col span={8}>
                  <NumberFormat
                    disabled={!showCarcassEntryByHerd}
                    customInput={Input}
                    value={herd.numberOfDays || ""}
                    placeholder={t.numberOfDaysPlaceholder}
                    addonAfter="d"
                    decimalScale={0}
                    decimalSeparator=","
                    thousandSeparator="."
                    fixedDecimalScale={true}
                    onValueChange={({ floatValue }) =>
                      handleHerdDataChange(herd.id, "numberOfDays", floatValue)
                    }
                    style={{ textAlign: "center" }}
                  />
                </Col>
              )}
            </Row>
          ))}
          {showUniqueRow() && (
            <Row type="flex" gutter={8} style={{ marginTop: 8 }}>
              <Col span={8}></Col>
              <Col span={8}>
                <NumberFormat
                  style={{ textAlign: "center" }}
                  addonAfter="%"
                  customInput={Input}
                  value={uniqueData.carcassHarnessingPercentage || ""}
                  placeholder={t.carcassEntryPercentagePlaceholder}
                  decimalScale={2}
                  decimalSeparator=","
                  thousandSeparator="."
                  fixedDecimalScale={true}
                  onValueChange={({ floatValue }) =>
                    handleUniqueDataChange(
                      "carcassHarnessingPercentage",
                      floatValue > 100 ? 100 : floatValue,
                    )
                  }
                />
              </Col>
              {partnershipType === "ARROBA_PRODUCTION" && (
                <Col span={8}>
                  <NumberFormat
                    style={{ textAlign: "center" }}
                    addonAfter="d"
                    customInput={Input}
                    value={uniqueData.numberOfDays || ""}
                    placeholder={t.numberOfDaysPlaceholder}
                    decimalScale={0}
                    decimalSeparator=","
                    thousandSeparator="."
                    fixedDecimalScale={true}
                    onValueChange={({ floatValue }) =>
                      handleUniqueDataChange("numberOfDays", floatValue)
                    }
                  />
                </Col>
              )}
            </Row>
          )}
        </Col>
        {/* Agreed Weight Type and Max Break */}
        {partnershipType === "CARCASS_HARNESSING" && (
          <>
            <Col span={4}>
              <Row>
                <label
                  className={
                    errors.partnershipContract?.agreedWeightType &&
                    submitCount > 0
                      ? "error"
                      : ""
                  }
                >
                  {t.agreedWeightLabel}*
                </label>
              </Row>
              <Row>
                <Radio.Group
                  value={agreedWeightType}
                  onChange={handleAgreedWeightTypeChange}
                >
                  <Row type="flex" gutter={8}>
                    <Col>
                      <RadioButton value="pv_faz">
                        {t.agreedWeightPvFaz}
                      </RadioButton>
                    </Col>
                    <Col>
                      <RadioButton value="pv_conf">
                        {t.agreedWeightPvConf}
                      </RadioButton>
                    </Col>
                  </Row>
                </Radio.Group>
              </Row>
            </Col>
            <Col span={4}>
              <Row>
                <label
                  className={
                    errors.partnershipContract?.maxBreakPercentage &&
                    submitCount > 0
                      ? "error"
                      : ""
                  }
                >
                  {t.maxBreak}*
                </label>
              </Row>
              <Row>
                <NumberFormat
                  customInput={Input}
                  value={maxBreakPercentage !== null ? maxBreakPercentage : ""}
                  placeholder={t.maxBreakPlaceholder}
                  addonAfter="%"
                  decimalScale={2}
                  decimalSeparator=","
                  thousandSeparator="."
                  fixedDecimalScale={true}
                  onValueChange={({ floatValue }) =>
                    handleMaxBreakPercentageChange(
                      floatValue > 100 ? 100 : floatValue,
                    )
                  }
                />
              </Row>
            </Col>
          </>
        )}
        {/* Partner Percentage */}
        {partnershipType === "ARROBA_PRODUCTION" && (
          <Col span={8}>
            <Row>
              <label
                className={
                  errors.partnershipContract?.partnerPercentage &&
                  submitCount > 0
                    ? "error"
                    : ""
                }
              >
                {t.partnerPercentage}*
              </label>
            </Row>
            <Row>
              <NumberFormat
                customInput={Input}
                value={partnerPercentage !== null ? partnerPercentage : ""}
                placeholder={translation.defaultPlaceholder}
                addonAfter="%"
                decimalScale={2}
                decimalSeparator=","
                thousandSeparator="."
                fixedDecimalScale={true}
                onValueChange={({ floatValue }) =>
                  handlePartnerPercentageChange(
                    floatValue > 100 ? 100 : floatValue,
                  )
                }
              />
            </Row>
          </Col>
        )}
      </Row>
    </Card>
  );
};

export default PartnershipCard;
